import { useQuery } from '@apollo/client';
import { Typography } from '@mui/material';
import { useGridApiRef } from '@mui/x-data-grid-pro';
import { DateRange } from '@mui/x-date-pickers-pro';
import { RuleGroupType } from '@procureai/query-builder';
import { Widget } from '@procureai/ui-mui';
import { formatDate } from '@procureai/util';
import { isNotNil } from 'ramda';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Union } from 'ts-toolbelt';

import {
  AstroDateRangeInput,
  AstroSortDirection,
  AstroUserAvailabilityOrderField,
  UserAvailabilityListQuery,
} from '@/generated/gql/graphql';
import { I18nNamespace, UserDomainType } from '~/common/types';
import { CircularProgressCentered } from '~/components/CircularProgressCentered/CircularProgressCentered';
import {
  DataGridWidgetHeader,
  ExportButton,
  useDataGridExportLocal,
  useDataGridRemoteState,
} from '~/components/DataGridCustom';

import { USER_AVAILABILITY_LIST_QUERY } from './UserAvailabilityQuery.gql';
import { useAvailabilityDataGridConfig, USER_COLUMN_FIELD } from './UserAvailabilityWidget.dataGrid';
import { useAvailabilitySummary } from './UserAvailabilityWidget.hooks';
import { DataGridProStyled, WidgetContentStyled } from './UserAvailabilityWidget.styled';

const EMPTY_NODES: UserAvailabilityListQuery['astronaut']['userAvailability']['nodes'] = [];
const EMPTY_COLUMNS: UserAvailabilityListQuery['astronaut']['userAvailability']['columns'] = [];

export interface UserAvailabilityWidgetProps {
  dateRange?: Union.Nullable<DateRange<Date>>;
  filter?: RuleGroupType;
  filterCount?: number;
}

export const UserAvailabilityWidget = ({ filter, dateRange: dateRangProp }: UserAvailabilityWidgetProps) => {
  const apiRef = useGridApiRef();
  const { t } = useTranslation(I18nNamespace.widgets, { keyPrefix: 'UserAvailabilityWidget' });

  const [dateFrom, dateTo] = dateRangProp || [];

  const dateRange: Union.Nullable<AstroDateRangeInput> = useMemo(() => {
    return dateFrom && dateTo ? { from: formatDate(dateFrom), to: formatDate(dateTo) } : null;
  }, [dateFrom, dateTo]);

  const { data, loading: queryLoading } = useQuery(USER_AVAILABILITY_LIST_QUERY, {
    variables: {
      filter,
      dateRange,
      // ordering is done client-side, but for export the data is used loaded from the server
      order: {
        field: AstroUserAvailabilityOrderField.userLastName,
        sort: AstroSortDirection.asc,
      },
    },
  });

  const rows = data?.astronaut.userAvailability?.nodes || EMPTY_NODES;
  const columns = data?.astronaut.userAvailability?.columns || EMPTY_COLUMNS;
  const config = useAvailabilityDataGridConfig({ columns });
  const totalCount = data?.astronaut.userAvailability?.totalCount;
  const pinnedRows = useAvailabilitySummary({ columns, data: rows });

  const { loading: loadingRemoteState, gridProps } = useDataGridRemoteState({
    domain: UserDomainType.USER_AVAILABILITY,
    apiRef,
    config,
    skip: queryLoading,
  });

  const { exportStart, exporting } = useDataGridExportLocal({
    data: rows,
    config,
    apiRef,
  });

  const loading = queryLoading || loadingRemoteState;

  return (
    <Widget hover={false}>
      <DataGridWidgetHeader
        left={
          <Typography variant="body2">
            {isNotNil(totalCount) ? t('queryResults', { count: totalCount }) : ''}
          </Typography>
        }
        right={<ExportButton loading={exporting} onClick={exportStart} />}
      />
      <WidgetContentStyled disablePadding>
        {loading ? (
          <CircularProgressCentered sx={{ position: 'relative' }} />
        ) : (
          <DataGridProStyled
            sx={{
              // is needed to limit the height to the size of the parent flex-container
              // https://github.com/mui/mui-x/issues/8547
              overflow: 'hidden',
            }}
            hideFooter
            disableColumnMenu
            disableColumnReorder
            disableColumnResize
            getRowHeight={() => 62}
            rows={rows || []}
            pinnedRows={pinnedRows}
            {...gridProps}
            initialState={{ pinnedColumns: { left: [USER_COLUMN_FIELD] }, ...gridProps.initialState }}
          />
        )}
      </WidgetContentStyled>
    </Widget>
  );
};
